import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isPerfMetricsEnabled } from '../lib/debug/performance';
import { PredefinedMilisecondsTypeEnum } from '../lib/enums';
import { CharacterEnum } from '../lib/enums/character.enum';
import { SymbolAndOptionalExchangeCode } from '../lib/models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../lib/models/gateway/types';
import { RootState } from '../lib/store';
import { subscribeSymbolPriceAndChartData } from '../lib/store/common-actions';
import {
  chartSubscription,
  instrumentSnapshotSubscription,
  marketDataSubscribeTradeSubscription,
  nbboSubscription,
  symbolStatusSubscription,
} from '../lib/store/market-data/subscriptionHelpers';
import { hasValidCookie } from '../lib/store/selectors';
import BigChartCache from '../lib/store-util/BigChartCache';
import SmallChartDataCache from '../lib/store-util/SmallChartDataCache';
import SubscriptionTimingTracker from '../lib/store-util/SubscriptionTimingTracker';
import TradePriceCache from '../lib/store-util/TradePriceCache';
import { chartingRangeToGatewaySummaryBucket } from '../lib/util/GatewayHelpers';

/** ~5s at 200ms per poll: a large watchlist on a slow link needs longer than 2s to return lineClose. */
const INSTRUMENT_SNAPSHOT_MAX_ATTEMPTS = 25;
const CHART_DATA_RETRY_LIMIT = 5;

interface UseSubscriptionManagerProps {
  symbols: SymbolAndOptionalExchangeCode[];
  buckets?: GWMarketDataSummaryBucket[];
  currentRange?: string; // For dynamic range(1D, 1W, 1M, 3M, 1Y, 5Y)
  isBigChart?: boolean;
  enabled?: boolean;
  /** Names this batch in the ?debug=performance subscription-timing panel. */
  subscriptionLabel?: string;
}

interface LineCloseAndInstrumentSnapshotStatus {
  hasInstrumentSnapshotData: boolean;
  hasAnyLineClose: boolean;
}

const executeSubscriptions = (
  symbols: SymbolAndOptionalExchangeCode[],
  bucketsToUse: GWMarketDataSummaryBucket[],
  isBigChart: boolean,
) => {
  marketDataSubscribeTradeSubscription(symbols);
  if (isBigChart) nbboSubscription(symbols);
  symbolStatusSubscription(symbols);
  chartSubscription(
    symbols,
    bucketsToUse,
    false,
    isBigChart,
  );
};

const cleanupPreviousBigChartSubscription = (
  isBigChart: undefined | boolean,
  previousSymbols: SymbolAndOptionalExchangeCode[],
  previousBuckets: GWMarketDataSummaryBucket[],
):void => {
  if (!isBigChart || previousSymbols.length === 0) {
    return;
  }

  if (previousBuckets.length > 0) {
    chartSubscription(previousSymbols, previousBuckets, true, isBigChart);
  }
};

const getSubscriptionSymbolKey = (item: SymbolAndOptionalExchangeCode): string => (
  [
    item.symbol,
    item.stockExchangeCode || '',
    item.countryDesignation || '',
  ].join(CharacterEnum.Pipe)
);

export const useSubscriptionManager = ({
  symbols,
  buckets = [GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths],
  currentRange,
  isBigChart = false,
  enabled = true,
  subscriptionLabel,
}: UseSubscriptionManagerProps) => {
  const dispatch = useDispatch();
  const hasCookieSession = useSelector<RootState, boolean>((state: RootState) => hasValidCookie(state));
  const previousSymbolsRef = useRef<SymbolAndOptionalExchangeCode[]>([]);
  const previousBucketsRef = useRef<GWMarketDataSummaryBucket[]>([]);
  const retryTimersRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    if (!enabled || !hasCookieSession || symbols.length === 0) {
      return;
    }

    // Determine buckets to use - prioritize currentRange over static buckets
    const bucketsToUse = currentRange && currentRange !== 'YTD' && currentRange !== 'ALL'
      ? [chartingRangeToGatewaySummaryBucket(currentRange as any)]
      : buckets;

    const currentSymbolKeys = symbols.map(getSubscriptionSymbolKey).sort().join(CharacterEnum.Comma);
    const previousSymbolKeys = previousSymbolsRef.current
      .map(getSubscriptionSymbolKey).sort().join(CharacterEnum.Comma);

    // Check if symbols or buckets have actually changed
    const symbolsChanged = currentSymbolKeys !== previousSymbolKeys;
    const bucketsChanged = JSON.stringify(bucketsToUse) !== JSON.stringify(previousBucketsRef.current);

    if (symbolsChanged || bucketsChanged) {
      // Clear pending retry timers from previous subscriptions
      retryTimersRef.current.forEach(timer => clearTimeout(timer));
      retryTimersRef.current = [];
      cleanupPreviousBigChartSubscription(
        isBigChart,
        previousSymbolsRef.current,
        previousBucketsRef.current,
      );
      // Point the chart at the new symbol rather than clearing: a symbol opened before renders
      // from its cached ranges instead of waiting on a fresh round trip.
      if (isBigChart && symbolsChanged && symbols.length > 0) {
        BigChartCache.setActiveSymbol(symbols[0].symbol);
      }

      // Symbols dropped from the list keep streaming until the component unmounts unless they are
      // explicitly unsubscribed here - a watchlist edit would otherwise leak a subscription per removal.
      if (symbolsChanged && previousSymbolsRef.current.length > 0) {
        const currentKeys = new Set(symbols.map(getSubscriptionSymbolKey));
        const removedSymbols = previousSymbolsRef.current.filter(
          item => !currentKeys.has(getSubscriptionSymbolKey(item)),
        );

        if (removedSymbols.length > 0) {
          instrumentSnapshotSubscription(removedSymbols, true);
          marketDataSubscribeTradeSubscription(removedSymbols, true);
          if (isBigChart) nbboSubscription(removedSymbols, true);
          symbolStatusSubscription(removedSymbols, true);
          if (!isBigChart && previousBucketsRef.current.length > 0) {
            previousBucketsRef.current.forEach(bucket => {
              chartSubscription(removedSymbols, [ bucket ], true, false);
            });
          }
        }
      }

      // Dispatch action to trigger chart data loading for big charts
      if (isBigChart && symbolsChanged) {
        dispatch(subscribeSymbolPriceAndChartData(symbols));
      }

      // Debug-only: timestamp this batch so the timing panel can measure the round trip
      if (subscriptionLabel && !isBigChart && isPerfMetricsEnabled()) {
        SubscriptionTimingTracker.markSent(subscriptionLabel, symbols.map(item => item.symbol));
      }

      // Ensure instrument snapshot (lineClose data) loads first for ALL charts
      if (symbols.length > 0) {
        instrumentSnapshotSubscription(symbols);

        let attempts = 0;

        const waitForInstrumentSnapshot = (): void => {
          attempts++;// eslint-disable-line no-plusplus

          // Check each symbol's instrument snapshot status
          const symbolStatuses = symbols.map(symbol => {
            const tradePrice = TradePriceCache.get(symbol.symbol);
            const smallChartData = SmallChartDataCache.get(symbol.symbol);

            // Check if we have lineClose from instrument snapshot OR fallback from chart data
            const lineCloseFromInstrument = tradePrice?.priceData?.lineClose;
            const lineCloseFromChart = smallChartData?.closeValues?.[0]; // First close value from chart data
            const hasLineCloseFromInstrument = lineCloseFromInstrument != null;
            const hasLineCloseFromChart = lineCloseFromChart != null;
            const hasAnyLineClose = hasLineCloseFromInstrument || hasLineCloseFromChart;

            return {
              symbol: symbol.symbol,
              hasInstrumentSnapshotData: tradePrice?.hasInstrumentSnapshotData === true,
              hasAnyLineClose,
            } as LineCloseAndInstrumentSnapshotStatus;
          });

          const allSymbolsHaveInstrumentSnapshot = symbolStatuses.every(status => status.hasInstrumentSnapshotData);
          const allSymbolsHaveAnyLineClose = symbolStatuses.every(status => status.hasAnyLineClose);

          // Proceed if we have instrument snapshot data OR if we have any lineClose data (from instrument or chart)
          const allSymbolsReady = allSymbolsHaveInstrumentSnapshot || allSymbolsHaveAnyLineClose;

          if (allSymbolsReady || attempts >= INSTRUMENT_SNAPSHOT_MAX_ATTEMPTS) {
            executeSubscriptions(symbols, bucketsToUse, isBigChart);

            // Retry chart subscription with multiple attempts if small chart data doesn't arrive
            const retryChartDataSubscription = (attempt: number) => {
              const missingSymbols = symbols.filter(item => {
                const chart = SmallChartDataCache.get(item.symbol);
                return !chart?.isReady;
              });

              // Early exit if all charts are ready, or stop once the retry budget is spent
              if (missingSymbols.length === 0 || attempt > CHART_DATA_RETRY_LIMIT) {
                return;
              }

              // Retry: unsubscribe first, then resubscribe (only for missing symbols)
              chartSubscription(missingSymbols, bucketsToUse, true, isBigChart); // unsubscribe
              const resubscribeTimer = setTimeout(() => {
                chartSubscription(missingSymbols, bucketsToUse, false, isBigChart); // resubscribe
              }, PredefinedMilisecondsTypeEnum.twoHundredMilliseconds);
              retryTimersRef.current.push(resubscribeTimer);

              // Schedule next retry
              const nextRetryTimer = setTimeout(
                () => retryChartDataSubscription(attempt + 1),
                PredefinedMilisecondsTypeEnum.oneSecond,
              );
              retryTimersRef.current.push(nextRetryTimer);
            };

            const initialRetryTimer = setTimeout(
              () => retryChartDataSubscription(1),
              PredefinedMilisecondsTypeEnum.oneSecond,
            );
            retryTimersRef.current.push(initialRetryTimer);
          } else {
            const waitTimer = setTimeout(
              waitForInstrumentSnapshot,
              PredefinedMilisecondsTypeEnum.twoHundredMilliseconds,
            );
            retryTimersRef.current.push(waitTimer);
          }
        };

        const initialWaitTimer = setTimeout(
          waitForInstrumentSnapshot,
          PredefinedMilisecondsTypeEnum.twoHundredMilliseconds,
        );
        retryTimersRef.current.push(initialWaitTimer);
      }

      previousSymbolsRef.current = [...symbols];
      previousBucketsRef.current = [...bucketsToUse];
    }
  }, [symbols, buckets, currentRange, isBigChart, enabled, hasCookieSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => () => {
    // Clear all pending retry timers
    retryTimersRef.current.forEach(timer => clearTimeout(timer));
    retryTimersRef.current = [];

    if (previousSymbolsRef.current.length > 0 && hasCookieSession) {
      const previousSymbols = previousSymbolsRef.current;
      const previousBuckets = previousBucketsRef.current.length > 0
        ? previousBucketsRef.current
        : buckets;

      // Schedule delayed unsubscriptions for all services
      instrumentSnapshotSubscription(previousSymbols, true);

      marketDataSubscribeTradeSubscription(previousSymbols, true);
      if (isBigChart) nbboSubscription(previousSymbols, true);
      symbolStatusSubscription(previousSymbols, true);
      previousBuckets.forEach(bucket => {
        chartSubscription(
          previousSymbols,
          [bucket],
          true,
          isBigChart,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on unmount
};
